import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import type React from "react";
import { useCallback, useId } from "react";
import { Controller, useForm } from "react-hook-form";
import { ADMIN_ROLES } from "@/config/constants";
import { useCognitoOperations, useSnackbar } from "@/shared/hooks";

import { type SignUpFormValue, signUpSchema } from "@/shared/schemas/signUpSchema";

export interface CreateDialogProps {
	open: boolean;
	handleClose: () => void;
	onCreateUser: () => void | Promise<void>;
}

export function CreateAdminsUserDialog({
	open,
	handleClose,
	onCreateUser,
}: CreateDialogProps): React.ReactElement {
	const { createUser } = useCognitoOperations();
	const { showSuccess, showError } = useSnackbar();
	const emailId = useId();
	const roleId = useId();
	const departmentId = useId();
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
		control,
	} = useForm<SignUpFormValue>({
		resolver: zodResolver(signUpSchema),
		defaultValues: {
			role: "実行者",
			email: "",
			department: "",
		},
	});

	const handleCancel = useCallback(() => {
		reset();
		handleClose();
	}, [reset, handleClose]);

	const handleCreateAdminUser = useCallback(
		async (data: SignUpFormValue) => {
			try {
				await createUser(data);
				showSuccess("ユーザー招待メールを送信しました。");
				await onCreateUser();
				handleClose();
				reset();
			} catch (error) {
				console.error("Error creating user:", error);
				showError("エラーが発生しました。内容を確認してください。");
			}
		},
		[createUser, showSuccess, onCreateUser, handleClose, reset, showError],
	);

	return (
		<Box>
			<Modal open={open} onClose={handleClose}>
				<Box
					component="form"
					onSubmit={handleSubmit(handleCreateAdminUser)}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: 400,
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 3,
						borderRadius: 2,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 2,
					}}
				>
					<IconButton
						onClick={handleCancel}
						sx={{
							position: "absolute",
							top: 8,
							right: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
					<Box sx={{ width: "100%", display: "flex", justifyContent: "left", mb: 1 }}>
						<Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
							新規ユーザー追加
						</Typography>
					</Box>
					<TextField
						id={emailId}
						label="メールアドレス"
						placeholder="email@example.com"
						required
						{...register("email")}
						error={Boolean(errors.email)}
						helperText={errors.email?.message}
						sx={{ width: "100%", mb: 2 }}
					/>
					<Controller
						name="role"
						control={control}
						render={({ field }) => (
							<FormControl sx={{ width: "100%", mb: 2 }}>
								<InputLabel id={`${roleId}-label`}>権限</InputLabel>
								<Select labelId={`${roleId}-label`} id={roleId} label="権限" required {...field}>
									{ADMIN_ROLES.map((role) => (
										<MenuItem key={role} value={role}>
											{role}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					/>
					<TextField
						id={departmentId}
						label="所属"
						{...register("department")}
						sx={{ width: "100%" }}
						multiline
						minRows={3}
						slotProps={{
							input: {
								sx: {
									padding: "0px",
								},
							},
						}}
					/>
					<Box sx={{ display: "flex", justifyContent: "left", width: "100%" }}>
						<Button
							variant="contained"
							type="submit"
							sx={{ mt: 2, width: 100, backgroundColor: "white", color: "#393a3b" }}
						>
							追加
						</Button>
					</Box>
				</Box>
			</Modal>
		</Box>
	);
}
